import React, { useEffect, useRef, useState, useCallback } from "react";
import "leaflet/dist/leaflet.css";

// Fix leaflet's default icon path issue with bundlers
let L;
if (typeof window !== "undefined") {
  L = require("leaflet");
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  });
}

const NOMINATIM_BASE = "https://nominatim.openstreetmap.org";

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `${NOMINATIM_BASE}/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
    );
    return await res.json();
  } catch {
    return null;
  }
}

async function searchPlaces(query) {
  try {
    const res = await fetch(
      `${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`
    );
    return await res.json();
  } catch {
    return [];
  }
}

export default function LocationPickerMap({ value, onChange }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  // Track the last lat/lng we placed ourselves so we can ignore echo-backs
  const ownPositionRef = useRef(null);

  const [address, setAddress] = useState(value?.address ?? "");
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchDebounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  const defaultLat = value?.latitude ?? 21.4292064;
  const defaultLng = value?.longitude ?? 50.852764;

  // Place/move marker and optionally reverse-geocode
  const updateMarker = useCallback(async (lat, lng, skipAddress = false) => {
    if (!mapRef.current) return;

    ownPositionRef.current = { lat, lng };

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(mapRef.current);
      markerRef.current.on("dragend", async (e) => {
        const { lat: newLat, lng: newLng } = e.target.getLatLng();
        ownPositionRef.current = { lat: newLat, lng: newLng };
        setIsLoadingAddress(true);
        const data = await reverseGeocode(newLat, newLng);
        const addr = data?.display_name ?? "";
        setAddress(addr);
        setIsLoadingAddress(false);
        onChange?.({ latitude: newLat, longitude: newLng, address: addr, name: data?.name ?? addr });
      });
    }

    mapRef.current.panTo([lat, lng]);

    if (!skipAddress) {
      setIsLoadingAddress(true);
      const data = await reverseGeocode(lat, lng);
      const addr = data?.display_name ?? "";
      setAddress(addr);
      setIsLoadingAddress(false);
      onChange?.({ latitude: lat, longitude: lng, address: addr, name: data?.name ?? addr });
    }
  }, [onChange]);

  // Init map once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current, {
      center: [defaultLat, defaultLng],
      zoom: 13,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(mapRef.current);

    mapRef.current.on("click", async (e) => {
      const { lat, lng } = e.latlng;
      await updateMarker(lat, lng);
    });

    // Place initial marker — skip address fetch if address already provided
    updateMarker(defaultLat, defaultLng, !!value?.address);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        ownPositionRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync marker when parent updates value (e.g. edit mode load)
  useEffect(() => {
    const lat = value?.latitude;
    const lng = value?.longitude;
    if (!lat || !lng) return;

    // Skip if this is just an echo of a position we already set
    const own = ownPositionRef.current;
    if (own && own.lat === lat && own.lng === lng) return;

    if (mapRef.current) {
      // Move marker to the loaded position
      updateMarker(lat, lng, !!value?.address);
    }
    // If address is already provided by the parent, show it immediately
    if (value?.address) {
      setAddress(value.address);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.latitude, value?.longitude]);

  // Search with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      const results = await searchPlaces(searchQuery);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setIsSearching(false);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery]);

  const handleSuggestionClick = async (place) => {
    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);
    setSearchQuery(place.display_name);
    setSuggestions([]);
    setShowSuggestions(false);
    setAddress(place.display_name);
    if (mapRef.current) {
      await updateMarker(lat, lng, true);
      onChange?.({ latitude: lat, longitude: lng, address: place.display_name, name: place.name ?? place.display_name });
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        await updateMarker(coords.latitude, coords.longitude);
      },
      () => {}
    );
  };

  return (
    <div className="flex flex-col gap-0 w-full">
      {/* Search bar */}
      <div className="relative mb-2" ref={suggestionsRef}>
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg bg-white shadow-sm px-3 py-2 focus-within:border-theme focus-within:ring-1 focus-within:ring-theme transition-all">
          <i className="fa-solid fa-magnifying-glass text-gray-400 text-sm shrink-0" />
          <input
            type="text"
            className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400"
            placeholder="Search for a place..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          />
          {isSearching && (
            <i className="fa-solid fa-circle-notch fa-spin text-gray-400 text-sm shrink-0" />
          )}
          {searchQuery && !isSearching && (
            <button
              type="button"
              onClick={() => { setSearchQuery(""); setSuggestions([]); setShowSuggestions(false); }}
              className="text-gray-400 hover:text-gray-600 shrink-0"
            >
              <i className="fa-solid fa-xmark text-sm" />
            </button>
          )}
          <button
            type="button"
            onClick={handleMyLocation}
            title="Use my location"
            className="shrink-0 text-theme hover:text-theme/80 transition-colors ml-1"
          >
            <i className="fa-solid fa-location-crosshairs text-sm" />
          </button>
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && (
          <ul className="absolute z-[9999] top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-[200px] overflow-y-auto">
            {suggestions.map((place, i) => (
              <li key={i}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-start gap-2 border-b border-gray-50 last:border-0"
                  onClick={() => handleSuggestionClick(place)}
                >
                  <i className="fa-solid fa-location-dot text-theme mt-0.5 shrink-0 text-xs" />
                  <span className="line-clamp-2">{place.display_name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Map */}
      <div
        ref={mapContainerRef}
        className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm"
        style={{ height: "360px" }}
      />

      {/* Selected address info */}
      <div className="mt-2 flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 min-h-[44px]">
        <i className="fa-solid fa-location-dot text-theme mt-0.5 shrink-0 text-sm" />
        <div className="flex-1 min-w-0">
          {isLoadingAddress ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <i className="fa-solid fa-circle-notch fa-spin text-xs" />
              <span>Fetching address…</span>
            </div>
          ) : address ? (
            <p className="text-sm text-gray-700 leading-snug">{address}</p>
          ) : (
            <p className="text-sm text-gray-400 italic">Click on the map or search to select a location</p>
          )}
        </div>
      </div>
    </div>
  );
}
